import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError, switchMap, filter } from 'rxjs/operators';
import { FileUploadService } from './file-upload.service';
import { environment } from './global-environment';
import { 
  ControlItem, 
  ControlItemCreateRequest, 
  ControlItemUpdateRequest,
  ControlItemQueryParams,
  ControlItemResponse,
  AttachmentFile 
} from '../models/control-item.model';

@Injectable({
  providedIn: 'root'
})
export class ControlItemService {
  private apiUrl = environment.apiUrl + '/api/chemicals/control-items';

  constructor(
    private http: HttpClient,
    private fileUploadService: FileUploadService
  ) {}

  // Get control items list (JWT: companyid is retrieved from backend)
  getControlItems(params: ControlItemQueryParams): Observable<ControlItemResponse> {
    let httpParams = new HttpParams()
      .set('assistantNo', params.assistantNo)
      .set('isActive', params.isActive !== false ? 'true' : 'false');

    // === 加入 Data Scope 參數 ===
    if ((params as any).userid) {
      httpParams = httpParams.set('userid', (params as any).userid);
    }
    if ((params as any).dataScope) {
      httpParams = httpParams.set('dataScope', (params as any).dataScope);
    }

    // 加入其他篩選條件
    if (params.status) httpParams = httpParams.set('status', params.status);
    if (params.priority) httpParams = httpParams.set('priority', params.priority);
    if (params.assignedToId) httpParams = httpParams.set('assignedToId', params.assignedToId);
    if (params.category) httpParams = httpParams.set('category', params.category);
    if (params.pageSize) httpParams = httpParams.set('pageSize', params.pageSize.toString());
    if (params.continuationToken) httpParams = httpParams.set('continuationToken', params.continuationToken);

    console.log('呼叫 API URL:', `${this.apiUrl}/list`);
    console.log('HTTP 參數:', httpParams.toString());
    
    return this.http.get<ControlItemResponse>(`${this.apiUrl}/list`, { params: httpParams }).pipe(
      map(response => {
        // 如果需要做日期或標籤篩選，在前端處理
        if (response.success && response.data && Array.isArray(response.data)) {
          let items = response.data as ControlItem[];

          // 日期範圍篩選
          if (params.dueDateFrom || params.dueDateTo) {
            items = items.filter(item => {
              const dueDate = new Date(item.dueDate);
              if (params.dueDateFrom && dueDate < new Date(params.dueDateFrom)) return false;
              if (params.dueDateTo && dueDate > new Date(params.dueDateTo)) return false;
              return true;
            });
          }

          // 標籤篩選
          if (params.tags && params.tags.length > 0) {
            items = items.filter(item => 
              item.tags && params.tags!.some(tag => item.tags!.includes(tag))
            );
          }

          return {
            ...response,
            data: items,
            totalCount: items.length
          };
        }
        return response;
      }),
      catchError(error => {
        console.error('Error loading control items:', error);
        return of({
          success: false,
          error: error.message || 'Failed to load control items',
          data: []
        });
      })
    );
  }

  // Get single control item (JWT: companyid is retrieved from backend)
  getControlItem(id: string): Observable<ControlItemResponse> {
    return this.http.get<ControlItemResponse>(`${this.apiUrl}/${id}`).pipe(
      catchError(error => {
        console.error('Error loading control item:', error);
        return of({
          success: false,
          error: error.message || 'Failed to load control item'
        });
      })
    );
  }

  // 建立控制項目
  createControlItem(data: ControlItemCreateRequest, files?: File[]): Observable<ControlItemResponse> {
    // 如果有檔案需要上傳
    if (files && files.length > 0) {
      return this.uploadFiles(files).pipe(
        switchMap(attachments => {
          const newItem = {
            ...data,
            attachments: attachments
          };
          return this.http.post<ControlItemResponse>(`${this.apiUrl}/create`, newItem);
        }),
        catchError(error => {
          console.error('Error creating control item:', error);
          return of({
            success: false,
            error: error.message || 'Failed to create control item'
          });
        })
      );
    }

    return this.http.post<ControlItemResponse>(`${this.apiUrl}/create`, data).pipe(
      catchError(error => {
        console.error('Error creating control item:', error);
        return of({
          success: false,
          error: error.message || 'Failed to create control item'
        });
      })
    );
  }

  // Update control item (JWT: companyid is retrieved from backend)
  updateControlItem(id: string, data: ControlItemUpdateRequest, newFiles?: File[]): Observable<ControlItemResponse> {
    // 如果有新檔案需要上傳
    if (newFiles && newFiles.length > 0) {
      return this.uploadFiles(newFiles).pipe(
        switchMap(newAttachments => {
          const updateData = {
            ...data,
            attachments: newAttachments
          };
          return this.http.put<ControlItemResponse>(`${this.apiUrl}/${id}`, updateData);
        }),
        catchError(error => {
          console.error('Error updating control item:', error);
          return of({
            success: false,
            error: error.message || 'Failed to update control item'
          });
        })
      );
    }

    return this.http.put<ControlItemResponse>(`${this.apiUrl}/${id}`, data).pipe(
      catchError(error => {
        console.error('Error updating control item:', error);
        return of({
          success: false,
          error: error.message || 'Failed to update control item'
        });
      })
    );
  }

  // Delete control item (soft delete) (JWT: companyid is retrieved from backend)
  deleteControlItem(id: string, deletedBy: string): Observable<ControlItemResponse> {
    const params = new HttpParams()
      .set('deletedBy', deletedBy);
    
    return this.http.delete<ControlItemResponse>(`${this.apiUrl}/${id}`, { params }).pipe(
      catchError(error => {
        console.error('Error deleting control item:', error);
        return of({
          success: false,
          error: error.message || 'Failed to delete control item'
        });
      })
    );
  }

  // 上傳檔案到 Azure Blob Storage 使用後端 API
  private uploadFiles(files: File[]): Observable<AttachmentFile[]> {
    // 使用 FileUploadService 的批次上傳功能
    return this.fileUploadService.uploadMultipleFiles(files).pipe(
      // 過濾只保留成功的回應
      filter(event => event.type === 'success'),
      map(event => {
        const response = event.response;
        
        // 假設後端 API 回傳的格式
        if (response.success && response.files) {
          return response.files.map((fileInfo: any) => ({
            id: this.generateFileId(),
            name: fileInfo.originalName || fileInfo.filename,
            size: fileInfo.size,
            type: fileInfo.mimetype || fileInfo.type,
            uploadDate: new Date().toISOString(),
            url: fileInfo.url,
            blobName: fileInfo.blobName // 儲存 blob 名稱以便後續操作
          }));
        }
        return [];
      }),
      catchError(error => {
        console.error('Failed to upload files:', error);
        return of([]);
      })
    );
  }

  // 產生檔案 ID
  private generateFileId(): string {
    return `file_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
  }
}